import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import { CourtGridDataService } from '../court-grid-data.service';
import {
  CourtColumnModel,
  CourtDurationOption,
  CourtGridSelectionEvent,
  DayGridModel,
} from '../court-grid.model';
import { hasOverlapWithBlockedRanges, nearestRangeAfter, nearestRangeBefore } from '../court-grid-blocked-ranges.util';
import { addMinutesIso, findSegmentAt, minutesBetween } from '../court-grid-time.util';
import {
  LaneResizeStartEvent,
  LaneSelectionVm,
  LanesCourtColumnComponent,
  LanesFreeCellActivatedEvent,
  LanesFreeCellDragStartEvent,
} from './lanes-court-column/lanes-court-column.component';
import { AggregatedCourseSessionDto } from '../../../../service/court-availability-service';
import { VenueTimezoneService } from '../../../../service/venue-timezone.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { leadTimeBlockedToastText } from '../../../../shared/utils/format-lead-time';
import { LeadTimeNoticeComponent } from '../../../../shared/components/lead-time-notice/lead-time-notice.component';
import { parseLocalDate } from '../../../../shared/utils/date-format.util';

/**
 * Geometry per the "Booking-Container · Header Integration" design (day
 * variant 3a) and the "Platzbelegung · Optionen" design:
 * day view — 42px time column, one 30px court-name header row; the day
 * itself is named by the non-scrolling day-nav-header pill row ABOVE the
 * scrollbox (nav arrows + weekday/date/"Heute" pill), so the sticky in-grid
 * header stays a single row;
 * frame 1W (Wochenansicht v2) — 34px time column, 22px day row + 14px
 * court-label row, 8px gaps between days but only 2px between the court
 * lanes inside a day (day separation comes from the BIGGER gap, no lines).
 */
const DAY_GUTTER_WIDTH = 42;
const WEEK_GUTTER_WIDTH = 34;
const DAY_HEADER_HEIGHT = 30;
const WEEK_DAY_ROW_HEIGHT = 22;
const WEEK_COURT_ROW_HEIGHT = 14;
const WEEK_HEADER_HEIGHT = WEEK_DAY_ROW_HEIGHT + WEEK_COURT_ROW_HEIGHT;
/** Gap between the time column and day groups, and BETWEEN day groups. */
const GROUP_GAP = 8;
/** Gap between court lanes inside one day: day view mirrors the group gap,
 *  week view uses the design's tight 2px. */
const COURT_GAP_DAY = 8;
const COURT_GAP_WEEK = 2;
/** Below these per-lane widths the grid scrolls horizontally instead of
 *  shrinking lanes further. */
const MIN_LANE_WIDTH_DAY = 72;
const MIN_LANE_WIDTH_WEEK = 24;
const HOUR_H = 60;
const MOBILE_HOUR_H = 50;
const MOBILE_CONTAINER_BREAKPOINT = 480;
/** Autoscroll-to-now: context kept visible ABOVE the current time. */
const AUTO_SCROLL_CONTEXT_MINUTES = 120;
/** How often the "now" line/chip position is refreshed. */
const NOW_TICK_INTERVAL_MS = 60_000;
/** Now-chip collision rule: chip label suppressed within this many px of an hour tick. */
const NOW_CHIP_TICK_COLLISION_PX = 12;
/** Breathing room below the legend when fitting the wrapper to the first screen. */
const WRAPPER_BOTTOM_GAP_PX = 24;
const WRAPPER_MIN_HEIGHT_PX = 320;

type DragKind = 'move' | 'resize-top' | 'resize-bottom';

interface LanesSelection {
  courtId: string;
  courtName: string;
  dateStr: string;
  startTimeUtc: string;
  durationMinutes: number;
}

/** Live in-flight drag state — `live` is what the owning lane renders. */
interface LanesSelectionDrag {
  kind: DragKind;
  live: LanesSelection;
  isValid: boolean;
  lastValid: LanesSelection;
}

/** Plain (non-signal) bookkeeping for the pointermove hot path — mirrors the
 *  classic grid's DragImperativeState, minus the direct-DOM overlay writes:
 *  the lanes selection renders signal-driven inside its lane, and the drag
 *  signal only updates on SNAPPED tuple changes (lastSnappedKey gate), so the
 *  render cost is one change-detection pass per 30-minute snap, not per
 *  pointer event. */
interface DragImperativeState {
  kind: DragKind;
  pointerId: number;
  /** Distance from pointerdown Y to the selection's top edge — used by 'move'. */
  grabOffsetY: number;
  /** Pointer Y at gesture start — used by resize deltaMinutes calc. */
  startClientY: number;
  originCourtId: string;
  originCourtName: string;
  originDateStr: string;
  originStartMs: number;
  originEndMs: number;
  /** Frozen clamp bounds for resize — nearest blocked range edges around the origin span. */
  gapLoMs: number;
  gapHiMs: number;
  /** Frozen duration options at the origin start — reused for resize-bottom (start never moves). */
  frozenDurationOptions: CourtDurationOption[];
  /** Last committed (snapped) tuple key — gates ngZone.run() calls to real changes. */
  lastSnappedKey: string;
  /** Per-snapped-anchor cache of duration options during a resize-top drag. */
  resizeTopOptionsCache?: { anchorStartMs: number; options: CourtDurationOption[] };
}

/**
 * Root of the v2 "Vertikale Lanes" court-availability grid (Tagesansicht v2 /
 * Wochenansicht v2) — the dedicated implementation of the "Platzbelegung ·
 * Optionen" design's frames 1 and 1W, fully independent of the classic
 * app-court-time-grid so either can be removed without leftovers.
 *
 * Same selection contract AND the same drag interactions as the classic grid:
 * click a free interval to select, press-drag-release on a free cell to
 * create with a chosen length (mouse), drag the selection's body to move it
 * (also across courts/days), drag the top/bottom handles to resize. The
 * selection block renders INSIDE its lane (design's `col.sel`), so all drag
 * geometry lives in the lane's own coordinate space — the target lane is
 * hit-tested via document.elementFromPoint on [data-grid-col], exactly like
 * the classic grid's move drag.
 */
@Component({
  selector: 'app-court-lanes-grid',
  standalone: true,
  imports: [LanesCourtColumnComponent, LeadTimeNoticeComponent],
  templateUrl: './court-lanes-grid.component.html',
  styleUrls: ['./court-lanes-grid.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourtLanesGridComponent implements OnDestroy {
  // ---- Public input surface (mirrors app-court-time-grid) --------------------
  /** 1 day for Tagesansicht v2, 7 for Wochenansicht v2. */
  days = input<DayGridModel[]>([]);
  loading = input(false);
  /** Latest absolute UTC time a booking may start. null = no limit. */
  maxBookableUtc = input<Date | null>(null);
  /** Lead time in minutes, for toast/notice text. null = no limit configured. */
  leadTimeMinutes = input<number | null>(null);
  /** Interval granularity in minutes (matches the backend's aggregated-slots response). */
  intervalMinutes = input(30);
  /** All durations the category supports — passed straight through to CourtGridDataService. */
  availableIntervals = input<number[]>([]);
  /** Duration pushed in from the sibling grid-selection-panel's slider. */
  externalDurationMinutes = input<number | null>(null);

  courtSlotSelected = output<CourtGridSelectionEvent>();
  courseSessionClicked = output<AggregatedCourseSessionDto>();
  navigatePrev = output<void>();
  navigateNext = output<void>();

  // ---- Responsive geometry ----------------------------------------------------
  private readonly containerWidth = signal(0);

  private readonly isMobileWidth = computed(
    () => this.containerWidth() > 0 && this.containerWidth() < MOBILE_CONTAINER_BREAKPOINT,
  );

  pxPerMinute = computed(() => (this.isMobileWidth() ? MOBILE_HOUR_H : HOUR_H) / 60);

  isWeekView = computed(() => this.days().length > 1);

  gutterWidth = computed(() => (this.isWeekView() ? WEEK_GUTTER_WIDTH : DAY_GUTTER_WIDTH));

  headerHeightPx = computed(() => (this.isWeekView() ? WEEK_HEADER_HEIGHT : DAY_HEADER_HEIGHT));

  courtGap = computed(() => (this.isWeekView() ? COURT_GAP_WEEK : COURT_GAP_DAY));

  private readonly courtCount = computed(() => {
    const day = this.days().find(d => d.courts.length);
    return day ? day.courts.length : 0;
  });

  /** A day group never shrinks below this — beyond it the grid pans horizontally. */
  dayGroupMinWidth = computed(() => {
    const courts = Math.max(1, this.courtCount());
    const minLane = this.isWeekView() ? MIN_LANE_WIDTH_WEEK : MIN_LANE_WIDTH_DAY;
    return courts * minLane + (courts - 1) * this.courtGap();
  });

  innerMinWidth = computed(() => {
    const dayCount = this.days().length || 1;
    return this.gutterWidth() + dayCount * (GROUP_GAP + this.dayGroupMinWidth());
  });

  hasData = computed(() => this.days().some(d => d.courts.length > 0));

  // ---- Time axis --------------------------------------------------------------
  /** Widest-span day used as the canonical vertical scale (all days of a real
   *  venue share the same time-of-day opening hours). */
  private canonicalColumn = computed<CourtColumnModel | null>(() => {
    let best: CourtColumnModel | null = null;
    let bestSpan = -1;
    for (const day of this.days()) {
      const col = day.courts.find(c => c.dayStartUtc && c.dayEndUtc);
      if (!col) continue;
      const span = new Date(col.dayEndUtc).getTime() - new Date(col.dayStartUtc).getTime();
      if (span > bestSpan) {
        bestSpan = span;
        best = col;
      }
    }
    return best;
  });

  bodyHeightPx = computed(() => {
    const col = this.canonicalColumn();
    if (!col) return 0;
    return minutesBetween(col.dayStartUtc, col.dayEndUtc) * this.pxPerMinute();
  });

  totalHeightPx = computed(() => this.headerHeightPx() + this.bodyHeightPx());

  timeAxisTicks = computed<{ label: string; top: number }[]>(() => {
    const col = this.canonicalColumn();
    if (!col) return [];
    const startMs = new Date(col.dayStartUtc).getTime();
    const endMs = new Date(col.dayEndUtc).getTime();
    const pxPerMin = this.pxPerMinute();
    const headerH = this.headerHeightPx();
    const ticks: { label: string; top: number }[] = [];
    for (let ms = startMs; ms <= endMs; ms += 60 * 60_000) {
      ticks.push({
        label: this.formatTime(new Date(ms).toISOString()),
        top: headerH + ((ms - startMs) / 60_000) * pxPerMin,
      });
    }
    return ticks;
  });

  // ---- Current-time ("now") line -----------------------------------------------
  private readonly nowMs = signal(Date.now());
  private nowTickIntervalId: ReturnType<typeof setInterval> | null = null;

  /** Top offset (px, `.grid-inner` space) of the now line; null while no
   *  rendered day is today or "now" lies outside opening hours. */
  nowLineTop = computed<number | null>(() => {
    const nowMs = this.nowMs();
    const today = this.days().find(d => d.isToday);
    if (!today) return null;
    const column = today.courts.find(c => c.dayStartUtc && c.dayEndUtc);
    if (!column) return null;
    const dayStartMs = new Date(column.dayStartUtc).getTime();
    const dayEndMs = new Date(column.dayEndUtc).getTime();
    if (nowMs < dayStartMs || nowMs > dayEndMs) return null;
    return this.headerHeightPx() + ((nowMs - dayStartMs) / 60_000) * this.pxPerMinute();
  });

  /** Red current-time chip in the time column; empty label near an hour tick. */
  nowChip = computed<{ top: number; label: string } | null>(() => {
    const top = this.nowLineTop();
    if (top === null) return null;
    const collides = this.timeAxisTicks().some(
      tick => Math.abs(tick.top - top) < NOW_CHIP_TICK_COLLISION_PX,
    );
    return { top, label: collides ? '' : this.formatTime(new Date(this.nowMs()).toISOString()) };
  });

  // ---- Selection state (mirrors the classic grid's committed/drag model) ------
  private readonly committed = signal<LanesSelection | null>(null);
  private readonly drag = signal<LanesSelectionDrag | null>(null);

  private readonly activeSelection = computed<LanesSelection | null>(
    () => this.drag()?.live ?? this.committed(),
  );

  /** The selection as seen by ONE lane — null unless it lives on that court+day. */
  selectionFor(dateStr: string, courtId: string): LaneSelectionVm | null {
    const sel = this.activeSelection();
    if (!sel || sel.dateStr !== dateStr || sel.courtId !== courtId) return null;
    const dragState = this.drag();
    const endIso = addMinutesIso(sel.startTimeUtc, sel.durationMinutes);
    return {
      startTimeUtc: sel.startTimeUtc,
      durationMinutes: sel.durationMinutes,
      label: `${this.formatTime(sel.startTimeUtc)}–${this.formatTime(endIso)}`,
      dragging: dragState !== null,
      invalid: dragState?.isValid === false,
    };
  }

  // ---- View refs / wrapper sizing ----------------------------------------------
  private wrapperElRef = viewChild<ElementRef<HTMLElement>>('wrapperEl');
  private scrollContainerEl = viewChild<ElementRef<HTMLElement>>('scrollContainer');

  /** Measured viewport-fit cap (see the classic grid's identical rationale). */
  wrapperMaxHeight = signal<number | null>(null);

  // ---- Imperative drag hot-path state (plain fields, not signals) --------------
  private dragImperative: DragImperativeState | null = null;
  private pendingMoveEvent: PointerEvent | null = null;
  private dragRafId = 0;
  private activeDragCleanup: (() => void) | null = null;
  /** Set when a free-cell press starts a create-drag: the browser still fires
   *  a click on that cell after pointerup, which onFreeCellActivated() must
   *  swallow exactly once (or it re-commits the minimum duration over the
   *  dragged one). */
  private suppressNextFreeCellClick = false;

  private lastAutoScrollKey = '';
  private autoScrollRafId = 0;

  constructor(
    private gridDataService: CourtGridDataService,
    private venueTimezone: VenueTimezoneService,
    private ngZone: NgZone,
    private toastService: ToastService,
  ) {
    // Track the scroll container's width (mobile hour-height breakpoint).
    effect(onCleanup => {
      const elRef = this.scrollContainerEl();
      if (!elRef) return;
      const el = elRef.nativeElement;
      let ro: ResizeObserver;
      this.ngZone.runOutsideAngular(() => {
        ro = new ResizeObserver(entries => {
          const width = entries[0]?.contentRect.width ?? el.clientWidth;
          this.ngZone.run(() => this.containerWidth.set(width));
        });
        ro.observe(el);
      });
      this.containerWidth.set(el.clientWidth);
      onCleanup(() => ro?.disconnect());
    });

    // Fit the whole component (grid + legend) onto the first screen — same
    // measuring approach as the classic grid.
    effect(onCleanup => {
      const elRef = this.wrapperElRef();
      this.days(); // re-measure when a new dataset renders
      if (!elRef) return;
      const el = elRef.nativeElement;

      const measure = () => {
        const docTop = el.getBoundingClientRect().top + window.scrollY;
        const firstScreenRemainder = window.innerHeight - docTop - WRAPPER_BOTTOM_GAP_PX;
        const available =
          firstScreenRemainder >= window.innerHeight / 2
            ? firstScreenRemainder
            : window.innerHeight - WRAPPER_BOTTOM_GAP_PX;
        const next = Math.max(WRAPPER_MIN_HEIGHT_PX, Math.floor(available));
        if (next !== this.wrapperMaxHeight()) {
          this.ngZone.run(() => this.wrapperMaxHeight.set(next));
        }
      };

      let rafId = 0;
      const onResize = () => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(measure);
      };
      this.ngZone.runOutsideAngular(() => {
        rafId = requestAnimationFrame(measure);
        window.addEventListener('resize', onResize);
      });
      onCleanup(() => {
        cancelAnimationFrame(rafId);
        window.removeEventListener('resize', onResize);
      });
    });

    // Autoscroll to the current time once per loaded dataset.
    effect(() => {
      const days = this.days();
      const elRef = this.scrollContainerEl();
      if (!elRef || !days.some(d => d.courts.length > 0)) return;
      const key = days.map(d => d.date).join('|');
      if (key === this.lastAutoScrollKey) return;
      this.lastAutoScrollKey = key;
      untracked(() => this.scheduleAutoScrollToNow(elRef.nativeElement));
    });

    // Reconcile the committed selection against fresh data. Skipped while a
    // drag is in flight — nulling state mid-gesture would orphan the drag's
    // document listeners (same rationale as the classic grid); finishDrag()
    // re-validates against the latest grid model on release anyway.
    effect(() => {
      const grid = this.days();
      const isDragging = untracked(() => this.drag() !== null);
      if (isDragging) return;
      const committed = untracked(() => this.committed());
      if (!committed) return;
      if (!this.selectionStillValid(committed, grid)) {
        untracked(() => {
          this.committed.set(null);
          this.drag.set(null);
        });
      }
    });

    // Keep the now line/chip tracking real time.
    this.ngZone.runOutsideAngular(() => {
      this.nowTickIntervalId = setInterval(() => {
        this.ngZone.run(() => this.nowMs.set(Date.now()));
      }, NOW_TICK_INTERVAL_MS);
    });

    // Sync duration changes pushed in from the sibling grid-selection-panel.
    effect(() => {
      const duration = this.externalDurationMinutes();
      if (duration == null) return;
      const committed = untracked(() => this.committed());
      if (!committed || committed.durationMinutes === duration) return;
      untracked(() => this.applyExternalDuration(committed, duration));
    });
  }

  ngOnDestroy(): void {
    this.activeDragCleanup?.();
    cancelAnimationFrame(this.dragRafId);
    cancelAnimationFrame(this.autoScrollRafId);
    if (this.nowTickIntervalId !== null) {
      clearInterval(this.nowTickIntervalId);
      this.nowTickIntervalId = null;
    }
  }

  // ---- Header labels ----------------------------------------------------------
  /** Week view: compact "Mo" in each day group's sticky header; day view:
   *  full "Montag" for the day-nav-header pill above the scrollbox. */
  dayNameLabel(day: DayGridModel): string {
    const date = parseLocalDate(day.date);
    if (!date) return '';
    return date.toLocaleDateString('de-DE', { weekday: this.isWeekView() ? 'short' : 'long' });
  }

  dayDateLabel(day: DayGridModel): string {
    const date = parseLocalDate(day.date);
    if (!date) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.`;
  }

  navPrevTitle = computed(() => (this.isWeekView() ? 'Vorherige Woche' : 'Vorheriger Tag'));
  navNextTitle = computed(() => (this.isWeekView() ? 'Nächste Woche' : 'Nächster Tag'));

  onNavPrev(): void {
    this.navigatePrev.emit();
  }

  onNavNext(): void {
    this.navigateNext.emit();
  }

  // ---- Lead-time gating ---------------------------------------------------------
  private isOutOfLeadTime(startTimeUtc: string): boolean {
    const max = this.maxBookableUtc();
    if (!max) return false;
    return new Date(startTimeUtc).getTime() > max.getTime();
  }

  private notifyOutOfLeadTime(): void {
    const minutes = this.leadTimeMinutes();
    if (minutes == null) return;
    this.toastService.info(leadTimeBlockedToastText(minutes));
  }

  // ---- Free-cell click selection -------------------------------------------------
  onFreeCellActivated(event: LanesFreeCellActivatedEvent): void {
    // A press-drag-release create gesture already committed via finishDrag();
    // the browser's trailing click on the same cell must not re-commit the
    // minimum duration over the dragged one.
    if (this.suppressNextFreeCellClick) {
      this.suppressNextFreeCellClick = false;
      return;
    }
    if (this.isOutOfLeadTime(event.startTimeUtc)) {
      this.notifyOutOfLeadTime();
      return;
    }

    const availableDurations = this.gridDataService.getAvailableDurationsForCourt(
      event.courtId,
      event.dateStr,
      event.startTimeUtc,
      this.days(),
      this.availableIntervals(),
    );
    const chosen = availableDurations.find(d => d.available);
    if (!chosen) return;

    this.drag.set(null);
    this.committed.set({
      courtId: event.courtId,
      courtName: event.courtName,
      dateStr: event.dateStr,
      startTimeUtc: event.startTimeUtc,
      durationMinutes: chosen.duration,
    });
    this.emitSelection(event.courtId, event.courtName, event.dateStr, event.startTimeUtc, chosen.duration, availableDurations);
  }

  // ---- Create-drag: mouse press on a free cell, drag down to extend, release --
  /**
   * Press-drag-release selection on free cells (mouse only — touch must stay
   * free to pan the scrollbox; touch users tap to select and adjust via the
   * handles). Reuses the 'resize-bottom' drag session anchored at the pressed
   * cell, exactly like the classic grid: the selection appears on press at
   * the smallest bookable duration, dragging down extends its end, releasing
   * commits via finishDrag().
   */
  onFreeCellDragStarted(event: LanesFreeCellDragStartEvent): void {
    if (this.dragImperative) return;
    if (this.isOutOfLeadTime(event.startTimeUtc)) {
      this.notifyOutOfLeadTime();
      return;
    }

    const availableDurations = this.gridDataService.getAvailableDurationsForCourt(
      event.courtId,
      event.dateStr,
      event.startTimeUtc,
      this.days(),
      this.availableIntervals(),
    );
    const chosen = availableDurations.find(d => d.available);
    if (!chosen) return;

    // Stop text selection from the mouse drag; the trailing click is
    // swallowed via suppressNextFreeCellClick (preventDefault on pointerdown
    // does NOT suppress the click event).
    event.pointerEvent.preventDefault();
    this.suppressNextFreeCellClick = true;

    const base: LanesSelection = {
      courtId: event.courtId,
      courtName: event.courtName,
      dateStr: event.dateStr,
      startTimeUtc: event.startTimeUtc,
      durationMinutes: chosen.duration,
    };
    // Anchor the resize delta at the initial selection's END edge so the
    // selection's end tracks the pointer absolutely ("release where it
    // should end") instead of by relative displacement.
    const cellTop = (event.pointerEvent.currentTarget as HTMLElement).getBoundingClientRect().top;
    const endEdgeClientY = cellTop + chosen.duration * this.pxPerMinute();
    this.startDrag('resize-bottom', event.pointerEvent, base, 0, endEdgeClientY);
  }

  // ---- Course block activation ------------------------------------------------
  onCourseSessionClicked(session: AggregatedCourseSessionDto): void {
    this.courseSessionClicked.emit(session);
  }

  // ---- Move-drag: pointerdown on the selection block's body -------------------
  onSelectionBodyPointerDown(event: PointerEvent): void {
    const sel = this.activeSelection();
    if (!sel) return;
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.startDrag('move', event, sel, event.clientY - rect.top);
  }

  // ---- Resize-drag: pointerdown on a top/bottom handle -------------------------
  onSelectionResizeStarted(event: LaneResizeStartEvent): void {
    const sel = this.activeSelection();
    if (!sel) return;
    this.startDrag(event.kind, event.pointerEvent, sel, 0);
  }

  private startDrag(
    kind: DragKind,
    event: PointerEvent,
    base: LanesSelection,
    grabOffsetY: number,
    // Create-drag anchors the resize delta at the selection's end edge
    // instead of the actual press point (see onFreeCellDragStarted).
    startClientYOverride?: number,
  ): void {
    // One drag at a time — a second pointer must not overwrite the in-flight
    // session's state/listeners (same rule as the classic grid).
    if (this.dragImperative) return;

    const column = this.columnFor(base.dateStr, base.courtId);
    if (!column) return;

    const pointerId = event.pointerId;
    try {
      (event.currentTarget as HTMLElement).setPointerCapture(pointerId);
    } catch {
      // Pointer capture is best-effort — drag still works via document listeners.
    }

    const originStartMs = new Date(base.startTimeUtc).getTime();
    const originEndMs = originStartMs + base.durationMinutes * 60_000;
    const gapLoMs = nearestRangeBefore(originStartMs, column.blockedRanges)?.endMs
      ?? new Date(column.dayStartUtc).getTime();
    const gapHiMs = nearestRangeAfter(originEndMs, column.blockedRanges)?.startMs
      ?? new Date(column.dayEndUtc).getTime();

    const frozenDurationOptions = this.gridDataService.getAvailableDurationsForCourt(
      base.courtId,
      base.dateStr,
      base.startTimeUtc,
      this.days(),
      this.availableIntervals(),
    );

    this.dragImperative = {
      kind,
      pointerId,
      grabOffsetY,
      startClientY: startClientYOverride ?? event.clientY,
      originCourtId: base.courtId,
      originCourtName: base.courtName,
      originDateStr: base.dateStr,
      originStartMs,
      originEndMs,
      gapLoMs,
      gapHiMs,
      frozenDurationOptions,
      lastSnappedKey: '',
    };

    this.drag.set({
      kind,
      live: { ...base },
      isValid: true,
      lastValid: { ...base },
    });

    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      if (e.cancelable) e.preventDefault();
      this.pendingMoveEvent = e;
      if (!this.dragRafId) {
        this.dragRafId = requestAnimationFrame(() => {
          this.dragRafId = 0;
          this.processDragFrame();
        });
      }
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      // Process the exact release coordinates before committing — a pending
      // rAF frame from the last pointermove may not have run yet.
      this.pendingMoveEvent = e;
      this.processDragFrame();
      cleanup();
      this.finishDrag();
    };
    const onCancel = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      cleanup();
      this.abortDrag();
    };
    const cleanup = () => {
      cancelAnimationFrame(this.dragRafId);
      this.dragRafId = 0;
      this.pendingMoveEvent = null;
      this.activeDragCleanup = null;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
    };
    this.activeDragCleanup = cleanup;

    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('pointermove', onMove, { passive: false });
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onCancel);
    });
  }

  /** Hot path — runs outside Angular, coalesced to one execution per animation frame. */
  private processDragFrame(): void {
    const e = this.pendingMoveEvent;
    const d = this.dragImperative;
    const dragValue = this.drag();
    if (!e || !d || !dragValue) return;

    if (d.kind === 'move') {
      this.processMoveFrame(e, d, dragValue);
    } else {
      this.processResizeFrame(e, d, dragValue);
    }
  }

  private processMoveFrame(e: PointerEvent, d: DragImperativeState, dragValue: LanesSelectionDrag): void {
    // The dragging selection has pointer-events:none (see CSS), so the hit
    // test lands on the lane underneath — same approach as the classic grid.
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const laneEl = el?.closest('[data-grid-col]') as HTMLElement | null;
    if (!laneEl) return; // pointer outside any lane — keep last rendered position

    const key = laneEl.getAttribute('data-grid-col')!;
    const sepIdx = key.indexOf('__');
    if (sepIdx < 0) return;
    const dateStr = key.slice(0, sepIdx);
    const courtId = key.slice(sepIdx + 2);

    const column = this.columnFor(dateStr, courtId);
    if (!column) return;

    const laneRect = laneEl.getBoundingClientRect();
    const pxPerMin = this.pxPerMinute();
    const interval = this.intervalMinutes();
    const rawMinutes = (e.clientY - laneRect.top - d.grabOffsetY) / pxPerMin;
    const snappedMinutes = Math.round(rawMinutes / interval) * interval;

    const dayStartMs = new Date(column.dayStartUtc).getTime();
    const dayEndMs = new Date(column.dayEndUtc).getTime();
    const duration = dragValue.live.durationMinutes;

    let candidateStartMs = dayStartMs + snappedMinutes * 60_000;
    const maxStartMs = dayEndMs - duration * 60_000;
    candidateStartMs = Math.max(dayStartMs, Math.min(maxStartMs, candidateStartMs));
    const candidateStartIso = new Date(candidateStartMs).toISOString();

    // Snap gate FIRST — most frames exit here without re-running the options
    // scan or entering the zone (the previous frame rendered these values).
    const tupleKey = `${courtId}__${dateStr}__${candidateStartIso}__${duration}`;
    if (tupleKey === d.lastSnappedKey) return;
    d.lastSnappedKey = tupleKey;

    // Validity = the target duration is an actually-priced, offered option at
    // the candidate start (not just "no overlap").
    const optionsAtCandidate = this.gridDataService.getAvailableDurationsForCourt(
      courtId,
      dateStr,
      candidateStartIso,
      this.days(),
      this.availableIntervals(),
    );
    const isValid = !!optionsAtCandidate.find(o => o.duration === duration)?.available;

    const courtName = this.courtNameFor(courtId, dateStr) ?? dragValue.live.courtName;
    const live: LanesSelection = { courtId, courtName, dateStr, startTimeUtc: candidateStartIso, durationMinutes: duration };
    const lastValid = isValid ? live : dragValue.lastValid;

    this.ngZone.run(() => {
      this.drag.set({ kind: 'move', live, isValid, lastValid });
    });
  }

  private processResizeFrame(e: PointerEvent, d: DragImperativeState, dragValue: LanesSelectionDrag): void {
    const column = this.columnFor(d.originDateStr, d.originCourtId);
    if (!column) return;

    const pxPerMin = this.pxPerMinute();
    const interval = this.intervalMinutes();
    const deltaMinutes = (e.clientY - d.startClientY) / pxPerMin;

    let candidateStartMs: number;
    let candidateEndMs: number;
    let durationOptionsAtStart: CourtDurationOption[];

    if (d.kind === 'resize-top') {
      let newStartMs = d.originStartMs + deltaMinutes * 60_000;
      newStartMs = this.snapToInterval(newStartMs, column.dayStartUtc, interval);
      newStartMs = Math.max(d.gapLoMs, Math.min(d.originEndMs - interval * 60_000, newStartMs));
      candidateStartMs = newStartMs;
      candidateEndMs = d.originEndMs;
      // Start moved — durations recomputed from the new anchor, cached per
      // snapped anchor so frames between snap points skip the scan.
      if (d.resizeTopOptionsCache?.anchorStartMs !== candidateStartMs) {
        d.resizeTopOptionsCache = {
          anchorStartMs: candidateStartMs,
          options: this.gridDataService.getAvailableDurationsForCourt(
            d.originCourtId,
            d.originDateStr,
            new Date(candidateStartMs).toISOString(),
            this.days(),
            this.availableIntervals(),
          ),
        };
      }
      durationOptionsAtStart = d.resizeTopOptionsCache.options;
    } else {
      let newEndMs = d.originEndMs + deltaMinutes * 60_000;
      newEndMs = this.snapToInterval(newEndMs, column.dayStartUtc, interval);
      newEndMs = Math.max(d.originStartMs + interval * 60_000, Math.min(d.gapHiMs, newEndMs));
      candidateStartMs = d.originStartMs;
      candidateEndMs = newEndMs;
      // Start is frozen for a bottom-handle resize — reuse the options from drag start.
      durationOptionsAtStart = d.frozenDurationOptions;
    }

    const anchorStartIso = new Date(candidateStartMs).toISOString();
    const rawDuration = Math.round((candidateEndMs - candidateStartMs) / 60_000 / interval) * interval;
    const originDuration = (d.originEndMs - d.originStartMs) / 60_000;
    const nearest = this.nearestAvailableDuration(rawDuration, durationOptionsAtStart, originDuration);

    if (!nearest) {
      // Nothing bookable in range — mark invalid but keep dragging.
      const invalidLive: LanesSelection = {
        courtId: d.originCourtId,
        courtName: d.originCourtName,
        dateStr: d.originDateStr,
        startTimeUtc: anchorStartIso,
        durationMinutes: Math.max(interval, rawDuration),
      };
      const tupleKey = `invalid__${invalidLive.startTimeUtc}__${invalidLive.durationMinutes}`;
      if (tupleKey === d.lastSnappedKey) return;
      d.lastSnappedKey = tupleKey;
      this.ngZone.run(() => {
        this.drag.set({ kind: d.kind, live: invalidLive, isValid: false, lastValid: dragValue.lastValid });
      });
      return;
    }

    const tupleKey = `${d.originCourtId}__${d.originDateStr}__${anchorStartIso}__${nearest.duration}`;
    if (tupleKey === d.lastSnappedKey) return;
    d.lastSnappedKey = tupleKey;

    const live: LanesSelection = {
      courtId: d.originCourtId,
      courtName: d.originCourtName,
      dateStr: d.originDateStr,
      startTimeUtc: anchorStartIso,
      durationMinutes: nearest.duration,
    };
    this.ngZone.run(() => {
      this.drag.set({ kind: d.kind, live, isValid: true, lastValid: live });
    });
  }

  private finishDrag(): void {
    const dragValue = this.drag();
    this.dragImperative = null;

    // Safety net for the create-drag click suppression (see the classic
    // grid's identical rationale): the trailing click — when it fires at
    // all — arrives before any timer callback, so this only mops up a
    // stale flag, never races a legitimate click.
    if (this.suppressNextFreeCellClick) {
      setTimeout(() => (this.suppressNextFreeCellClick = false));
    }

    if (!dragValue) return;

    if (dragValue.isValid) {
      const live = dragValue.live;
      const availableDurations = this.gridDataService.getAvailableDurationsForCourt(
        live.courtId,
        live.dateStr,
        live.startTimeUtc,
        this.days(),
        this.availableIntervals(),
      );
      // The emit must happen INSIDE ngZone.run() so the parent's handler
      // triggers a change-detection pass (this runs from a document
      // pointerup listener registered outside Angular).
      this.ngZone.run(() => {
        this.committed.set({ ...live });
        this.drag.set(null);
        this.emitSelection(live.courtId, live.courtName, live.dateStr, live.startTimeUtc, live.durationMinutes, availableDurations);
      });
    } else {
      // Invalid drop — snap back to the committed selection; the CSS
      // transition on .lane-selection animates this.
      this.ngZone.run(() => this.drag.set(null));
    }
  }

  private abortDrag(): void {
    this.dragImperative = null;
    // A canceled pointer never produces a click — drop the suppression
    // immediately or it would swallow the next genuine activation.
    this.suppressNextFreeCellClick = false;
    this.ngZone.run(() => this.drag.set(null));
  }

  private snapToInterval(candidateMs: number, dayStartIso: string, interval: number): number {
    const dayStartMs = new Date(dayStartIso).getTime();
    const minutes = Math.round((candidateMs - dayStartMs) / 60_000 / interval) * interval;
    return dayStartMs + minutes * 60_000;
  }

  /** Nearest available duration; exact ties break toward the drag direction
   *  (same rule as the classic grid, so sparse duration sets don't stall). */
  private nearestAvailableDuration(
    rawDuration: number,
    options: CourtDurationOption[],
    originDuration?: number,
  ): CourtDurationOption | null {
    const available = options.filter(o => o.available);
    if (!available.length) return null;
    const growing = originDuration != null && rawDuration >= originDuration;
    return available.reduce((best, o) => {
      const dO = Math.abs(o.duration - rawDuration);
      const dBest = Math.abs(best.duration - rawDuration);
      if (dO < dBest) return o;
      if (dO > dBest) return best;
      if (originDuration == null) return best;
      return growing
        ? (o.duration > best.duration ? o : best)
        : (o.duration < best.duration ? o : best);
    });
  }

  private applyExternalDuration(committed: LanesSelection, duration: number): void {
    const availableDurations = this.gridDataService.getAvailableDurationsForCourt(
      committed.courtId,
      committed.dateStr,
      committed.startTimeUtc,
      this.days(),
      this.availableIntervals(),
    );
    const option = availableDurations.find(d => d.duration === duration && d.available);
    if (!option) return;
    this.committed.set({ ...committed, durationMinutes: duration });
    this.emitSelection(committed.courtId, committed.courtName, committed.dateStr, committed.startTimeUtc, duration, availableDurations);
  }

  private emitSelection(
    courtId: string,
    courtName: string,
    dateStr: string,
    startTimeUtc: string,
    durationMinutes: number,
    availableDurations: CourtDurationOption[],
  ): void {
    const column = this.columnFor(dateStr, courtId);
    const anchorSegment = column
      ? findSegmentAt(column.segments, new Date(startTimeUtc).getTime())
      : undefined;
    const durationOption = availableDurations.find(d => d.duration === durationMinutes);
    if (!column || !anchorSegment || !durationOption) return;

    this.courtSlotSelected.emit({
      courtId,
      courtName,
      dateStr,
      startTimeUtc,
      endTimeUtc: durationOption.endTimeUtc,
      durationMinutes,
      price: durationOption.price,
      aggregatedSlot: anchorSegment.aggregatedSlot,
      availableDurations,
    });
  }

  private selectionStillValid(sel: LanesSelection, days: DayGridModel[]): boolean {
    const day = days.find(d => d.date === sel.dateStr);
    const column = day?.courts.find(c => c.courtId === sel.courtId);
    if (!day || !column || !column.dayStartUtc || !column.dayEndUtc) return false;
    const startMs = new Date(sel.startTimeUtc).getTime();
    const endMs = startMs + sel.durationMinutes * 60_000;
    const dayStartMs = new Date(column.dayStartUtc).getTime();
    const dayEndMs = new Date(column.dayEndUtc).getTime();
    if (startMs < dayStartMs || endMs > dayEndMs) return false;
    return !hasOverlapWithBlockedRanges(startMs, endMs, column.blockedRanges);
  }

  private columnFor(dateStr: string, courtId: string): CourtColumnModel | undefined {
    return this.days().find(d => d.date === dateStr)?.courts.find(c => c.courtId === courtId);
  }

  private courtNameFor(courtId: string, dateStr: string): string | undefined {
    return this.columnFor(dateStr, courtId)?.courtName;
  }

  /**
   * Scrolls the grid viewport so the current time sits comfortably in view;
   * in week view also horizontally so today's day group starts next to the
   * sticky time column. Deferred one animation frame so the fresh layout
   * exists before scrollTo() clamps.
   */
  private scheduleAutoScrollToNow(el: HTMLElement): void {
    cancelAnimationFrame(this.autoScrollRafId);
    this.ngZone.runOutsideAngular(() => {
      this.autoScrollRafId = requestAnimationFrame(() => {
        const days = this.days();
        const todayIndex = days.findIndex(d => d.isToday);
        const anchorDay = todayIndex >= 0 ? days[todayIndex] : days[0];
        const anchorColumn = anchorDay?.courts.find(c => c.dayStartUtc && c.dayEndUtc);
        if (!anchorColumn) return;

        const pxPerMin = this.pxPerMinute();
        const dayStartMs = new Date(anchorColumn.dayStartUtc).getTime();
        const dayEndMs = new Date(anchorColumn.dayEndUtc).getTime();
        const spanMs = dayEndMs - dayStartMs;
        if (spanMs <= 0) return;
        const nowOffsetMs = (((Date.now() - dayStartMs) % spanMs) + spanMs) % spanMs;
        const nowTop = this.headerHeightPx() + (nowOffsetMs / 60_000) * pxPerMin;
        const top = Math.max(0, nowTop - AUTO_SCROLL_CONTEXT_MINUTES * pxPerMin);

        // Week view: measured day-group offset (flex-grown widths aren't
        // knowable arithmetically), minus the sticky time column's width.
        let left = 0;
        if (this.isWeekView() && todayIndex >= 0) {
          const groupEl = el.querySelector<HTMLElement>(`[data-day="${anchorDay.date}"]`);
          if (groupEl) left = Math.max(0, groupEl.offsetLeft - this.gutterWidth() - GROUP_GAP);
        }

        el.scrollTo({ top, left });
      });
    });
  }

  formatTime(isoInstant: string): string {
    try {
      return this.venueTimezone.formatTime(isoInstant);
    } catch {
      return '';
    }
  }
}
